import React, { useContext, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { convertUndefinedFields } from 'utils/helpers';
import {
	IEditModifierRestaurantPropertiesContext,
	EditModifierRestaurantPropertiesContext,
} from '../../editModifierRestaurantPropertiesState/editModifierRestaurantPropertiesContext';
import { updateModifierRestaurantProperties } from 'backend/modifiers';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import { useHistory } from 'react-router-dom';
import { SaveAsPatchModal } from './SaveAsPatchModal';
import { ServerErrorTypes } from 'types';
import axios from 'axios';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeTypeOfModalWindow,
		refetchRestPropsData,
		dataForSubmit,
		typeOfModalWindow,
		modifierId,
		showErrorModalWithInfo,
		errorResponseData,
	} = useContext<IEditModifierRestaurantPropertiesContext>(EditModifierRestaurantPropertiesContext);
	const history = useHistory();

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateModifierRestaurantProperties(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.MENU_CIRCULAR_REFERENCE_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit, showLoader, hideLoader, changeTypeOfModalWindow, showErrorModalWithInfo]);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, [changeTypeOfModalWindow]);

	const handleCloseModalWithRefetchRestPropsData = useCallback(() => {
		refetchRestPropsData(true);
		changeTypeOfModalWindow(null);
	}, [refetchRestPropsData, changeTypeOfModalWindow]);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
		changeTypeOfModalWindow(null);
	}, [showErrorModalWithInfo, changeTypeOfModalWindow]);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow || !!errorResponseData}>
			{typeOfModalWindow === 'toConfirmFieldsClearing' && (
				<ModalToConfirm
					onConsentEvent={() => history.push(`/menu-v2/modifiers/view?modifierID=${modifierId}`)}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="cancelSaveInfo"
				/>
			)}
			{typeOfModalWindow === 'toConfirmSubmit' && (
				<ModalToConfirm
					onConsentEvent={handleConfirm}
					onFailureEvent={handleCloseModal}
					isVisible={true}
					description="saveInfo"
				/>
			)}
			{typeOfModalWindow === 'success' && (
				<ModalSentSuccessfully
					onConsentEvent={handleCloseModalWithRefetchRestPropsData}
					isVisible={true}
					description="savedSuccessfully"
				/>
			)}
			{typeOfModalWindow === 'error' && (
				<ModalSentError onConsentEvent={handleCloseModal} isVisible={true} description="errorWhileSaving" />
			)}
			{typeOfModalWindow === 'saveAsPatch' && (
				<SaveAsPatchModal onCloseModal={handleCloseModalWithRefetchRestPropsData} />
			)}
			{typeOfModalWindow === 'saveAsPatch' && (
				<ModalSentSuccessfully
					onConsentEvent={handleCloseModal}
					isVisible={true}
					description="savedSuccessfully"
				/>
			)}
			{errorResponseData && (
				<ModalSentError
					onConsentEvent={handleCloseModalWithInfo}
					isVisible={true}
					description="MenuCircularReference"
					translationOptions={{
						name: errorResponseData.Metadata.modifierGroupName,
					}}
				/>
			)}
		</ModalWrapper>
	);
};
